import { Tab } from '@scale/scaleui';
import { useState, forwardRef, memo } from 'react';
import { ChatInteraction, ChatInteractionHandle } from '../../ChatInteraction';
import { InteractionDemoResponseViewerModal } from '../InteractionDemoResponseViewerModal';
import { IChatTaskFeatureConfig } from '@scale/shared/types/config';
import OutlierTaskHeader from '../../../OutlierTaskHeader';
import { shouldUseReviewHeader } from 'react-scale/components/scalers/tasks/Chat/utils';
import { useChatStoreExtract } from '../../../Chat/hooks/ChatStore.hooks';
import { TEST_CHAT_PROJECT, TEST_CHAT_USER_ID, TaskType } from '@scale/shared/defaults';
import { v4 as uuidv4 } from 'uuid';
import { useWorkerCommentsStore } from '../../hooks/useWorkerCommentsStore';
import useAutosave from 'react-scale/hooks/useAutosave';
import { ChatAutosave } from '@scale/shared/types/chat';
import { IProjectJson } from '@scale/shared/types/project';
import { useMultiTurnInteractionValidation } from '../../utils';
import { AssignmentJson } from '@scale/shared/assignmentJson/types/AssignmentJson';
import { Params } from '@scale/shared/types/params';
import { Claim } from '@scale/shared/types/queue';

type TaskPreviewProps = {
  chatTaskFeatureConfig: IChatTaskFeatureConfig;
};

const TaskPreview = forwardRef<ChatInteractionHandle, TaskPreviewProps>(function TaskPreview(
  { chatTaskFeatureConfig },
  ref,
) {
  const [isSubmittingTask] = useState(false);

  const config = useChatStoreExtract(state => state.config);
  const reviewLevel = useChatStoreExtract(state => state.reviewLevel);
  // TODO: @Lajos as a follow-up we need to restructure autosave and the validation
  const responses = useChatStoreExtract(state => state.responses);
  const workerComments = useWorkerCommentsStore(state => state.workerComments);
  const allStepsCompleted = useChatStoreExtract(state => state.allStepsCompleted);
  const completedSteps = useChatStoreExtract(state => state.completedSteps);
  const hasActiveStepResponseLinterError = useChatStoreExtract(
    state => state.hasActiveStepResponseLinterError,
  );
  const prefetchingInProgress = useChatStoreExtract(state => state.prefetchingInProgress);
  const taskID = uuidv4();
  const attemptId = uuidv4();

  const noop = () => {
    return Promise.resolve();
  };

  const assignment: AssignmentJson = {
    params: {} as unknown as Params,
    projectName: 'Demo task',
    reviewLevel,
    type: TaskType.Chat,
  };

  const tomorrow = new Date();
  tomorrow.setUTCDate(tomorrow.getUTCDate() + 1);

  const claim: Claim = {
    claimed: true,
    canPause: true,
    isPaused: false,
    maxClaim: tomorrow,
  };

  const project: IProjectJson = {
    name: 'Demo Project',
    _id: TEST_CHAT_PROJECT,
    taskType: TaskType.Chat,
    paramHistory: [],
    taskCustomer: 'Test customer',
  };

  const autosave = useAutosave<ChatAutosave>({
    id: taskID,
    attemptId,
    worker: TEST_CHAT_USER_ID,
    data: {
      responses,
      comments: workerComments,
    },
    taskType: TaskType.Chat,
    projectId: TEST_CHAT_PROJECT,
  });

  const { canSubmit, submitTooltip, canSubmitSBQ, submitTooltipSBQ } =
    useMultiTurnInteractionValidation({
      config,
      responses,
      isSubmitting: isSubmittingTask,
      isLoading: false,
      hasResponseLoadingError: false,
      allStepsCompleted,
      workerComments,
      reviewLevel,
      inlineFeedbackConfig: chatTaskFeatureConfig?.inlineFeedbackConfig,
      hasActiveStepResponseLinterError,
      completedSteps,
      prefetchingInProgress,
    });

  const isReviewMode = shouldUseReviewHeader(reviewLevel, true);

  return (
    <>
      <Tab.Panel>
        <OutlierTaskHeader
          className="h-14"
          showHeader
          taskId={taskID}
          projectId={TEST_CHAT_PROJECT}
          taskDescription="Complete the interaction"
          autosave={autosave}
          autosaveEnabled={false}
          isChatTaskWithComments={workerComments.length > 0}
          canSubmit={canSubmit}
          canSubmitSBQ={canSubmitSBQ}
          submitSBQTooltip={submitTooltipSBQ}
          assignment={assignment}
          config={config}
          handleSubmit={noop}
          isInstructionTask={false}
          isSubmittingTask={isSubmittingTask}
          isTestQuestion={false}
          onUserSkip={noop}
          responses={responses}
          submitTooltip={submitTooltip}
          onUserReport={noop}
          sharingEnabled={chatTaskFeatureConfig?.allowSharing}
          isReviewMode={isReviewMode}
          project={project}
          taskType={TaskType.Chat}
          claim={claim}
          canReturnToQueue={isReviewMode}
        />
        <div className="flex w-full justify-center my-4 bg-neutral-0">
          <ChatInteraction ref={ref} demoMode readOnly={false} enableLayoutOverride />
        </div>
        <InteractionDemoResponseViewerModal featureConfig={chatTaskFeatureConfig} />
      </Tab.Panel>
    </>
  );
});

export default memo(TaskPreview);
